import { Badge, Button, Modal } from '@/components/ui';
import {
  logsMongoService,
  type BusquedaLogsDto,
  type ConsultaLogs,
  type FiltroLog,
  type OperadorLog,
  type ResumenLogsDto,
  type ValoresCampoDto,
} from '@/services/logs-mongo.service';
import { ArrowDownUp, ChevronLeft, ChevronRight, Filter, ListFilter, Loader2, Search, Trash2, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

const OPERADORES: { op: OperadorLog; label: string }[] = [
  { op: 'igual', label: '=' },
  { op: 'distinto', label: '≠' },
  { op: 'empieza', label: 'empieza por' },
  { op: 'contiene', label: 'contiene' },
  { op: 'existe', label: 'existe' },
  { op: 'noExiste', label: 'no existe' },
];
const SIN_VALOR: OperadorLog[] = ['existe', 'noExiste'];
const NIVELES: Record<number, { label: string; variant: 'gray' | 'blue' | 'warning' | 'red' }> = {
  10: { label: 'trace', variant: 'gray' },
  20: { label: 'debug', variant: 'gray' },
  30: { label: 'info', variant: 'blue' },
  40: { label: 'warn', variant: 'warning' },
  50: { label: 'error', variant: 'red' },
  60: { label: 'fatal', variant: 'red' },
};
const PAGE_SIZE = 50;
const SUGERENCIAS = 8;

const formatoBytes = (b: number | null) => (b === null ? '—' : b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${(b / 1e3).toFixed(0)} KB`);
const formatoFecha = (ms?: number) => (ms ? new Date(ms).toLocaleString('es-CO') : '—');
const aMs = (local: string) => (local ? new Date(local).getTime() : undefined);

type Sugerencia = { tipo: 'campo'; campo: string; detalle: string } | { tipo: 'valor'; campo: string; valor: string; detalle: string };

/** Separa "campo:valor" del buscador. Sin ":" todavía se está escribiendo el campo. */
function partirEntrada(texto: string): { campo: string; valor: string | null } {
  const i = texto.indexOf(':');
  return i < 0 ? { campo: texto.trim(), valor: null } : { campo: texto.slice(0, i).trim(), valor: texto.slice(i + 1).trimStart() };
}

export function LogsMongoPage() {
  const [resumen, setResumen] = useState<ResumenLogsDto | null>(null);
  const [errorGeneral, setErrorGeneral] = useState<string | null>(null);

  const [filtros, setFiltros] = useState<FiltroLog[]>([]);
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');

  const [entrada, setEntrada] = useState('');
  const [sugerencias, setSugerencias] = useState<Sugerencia[]>([]);
  const [abierto, setAbierto] = useState(false);
  const [resaltado, setResaltado] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const [resultado, setResultado] = useState<BusquedaLogsDto | null>(null);
  const [consultaBuscada, setConsultaBuscada] = useState<string | null>(null);
  const [buscando, setBuscando] = useState(false);
  const [pagina, setPagina] = useState(1);
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');
  const [expandido, setExpandido] = useState<string | null>(null);

  const [opcionesCampo, setOpcionesCampo] = useState<string | null>(null);
  const [confirmarAbierto, setConfirmarAbierto] = useState(false);
  const [mensaje, setMensaje] = useState<string | null>(null);

  const consulta: ConsultaLogs = useMemo(
    () => ({ ...(filtros.length ? { filtros } : {}), desde: aMs(desde), hasta: aMs(hasta) }),
    [filtros, desde, hasta]
  );
  const claveConsulta = JSON.stringify(consulta);
  // El borrado solo se habilita sobre exactamente lo que se filtró y se está viendo.
  const resultadoVigente = resultado !== null && consultaBuscada === claveConsulta;

  const cargarResumen = () =>
    logsMongoService
      .resumen()
      .then(setResumen)
      .catch((e) => setErrorGeneral(e.message));
  useEffect(() => {
    void cargarResumen();
  }, []);

  // Autocompletar: sin ":" sugiere campos; con "campo:" sugiere sus valores más frecuentes.
  useEffect(() => {
    const { campo, valor } = partirEntrada(entrada);
    if (valor === null) {
      const q = campo.toLowerCase();
      setSugerencias(
        (resumen?.campos ?? [])
          .filter((c) => c.campo.toLowerCase().startsWith(q))
          .slice(0, SUGERENCIAS)
          .map((c) => ({ tipo: 'campo', campo: c.campo, detalle: `en ${c.enMuestra} de ${resumen?.tamanoMuestra} recientes` }))
      );
      setResaltado(0);
      return;
    }
    if (!campo) return;
    const t = setTimeout(() => {
      logsMongoService
        .valores({ ...consulta, campo, prefijo: valor || undefined, pageSize: SUGERENCIAS })
        .then((r) => {
          setSugerencias(r.items.map((i) => ({ tipo: 'valor', campo, valor: i.valor, detalle: i.cantidad.toLocaleString('es-CO') })));
          setResaltado(0);
        })
        .catch(() => setSugerencias([]));
    }, 250);
    return () => clearTimeout(t);
  }, [entrada, resumen, claveConsulta]);

  const agregarFiltro = (f: FiltroLog) => {
    setFiltros((prev) => [...prev, f]);
    setEntrada('');
    setAbierto(false);
    inputRef.current?.focus();
  };

  const elegir = (s: Sugerencia) => {
    if (s.tipo === 'campo') {
      setEntrada(`${s.campo}:`);
      inputRef.current?.focus();
    } else {
      agregarFiltro({ campo: s.campo, op: 'igual', valor: s.valor });
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setAbierto(true);
      setResaltado((i) => Math.min(i + 1, sugerencias.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setResaltado((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Escape') {
      setAbierto(false);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const s = abierto ? sugerencias[resaltado] : undefined;
      if (s) return elegir(s);
      // Sin sugerencia elegida: "campo:valor" escrito a mano se agrega tal cual.
      const { campo, valor } = partirEntrada(entrada);
      if (campo && valor) agregarFiltro({ campo, op: 'igual', valor });
    }
  };

  const buscar = async (page = 1, sentido = dir) => {
    setBuscando(true);
    setErrorGeneral(null);
    setMensaje(null);
    try {
      const r = await logsMongoService.buscar({ ...consulta, page, pageSize: PAGE_SIZE, dir: sentido });
      setResultado(r);
      setConsultaBuscada(claveConsulta);
      setPagina(page);
      setExpandido(null);
    } catch (e: any) {
      setErrorGeneral(e.message);
    } finally {
      setBuscando(false);
    }
  };

  const eliminar = async () => {
    if (!resultado) return;
    try {
      const r = await logsMongoService.eliminar({ ...consulta, totalEsperado: resultado.total });
      setMensaje(`Se eliminaron ${r.eliminados.toLocaleString('es-CO')} logs.`);
      setResultado(null);
      setConsultaBuscada(null);
      void cargarResumen();
    } catch (e: any) {
      setErrorGeneral(e.message);
    } finally {
      setConfirmarAbierto(false);
    }
  };

  const campoEntrada = partirEntrada(entrada).campo;
  const totalPaginas = resultado ? Math.max(1, Math.ceil(resultado.total / PAGE_SIZE)) : 1;

  return (
    <div className="space-y-5">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Limpieza de logs</h1>
          <p className="text-sm text-gray-500">
            Filtra los logs guardados en MongoDB, revísalos y decide qué eliminar. No se borra nada automáticamente.
          </p>
        </div>
        {resumen && (
          <div className="flex gap-4 text-sm">
            <div>
              <span className="block text-xs text-gray-500">Logs</span>
              <span className="font-semibold tabular-nums">{resumen.total.toLocaleString('es-CO')}</span>
            </div>
            <div>
              <span className="block text-xs text-gray-500">Tamaño</span>
              <span className="font-semibold tabular-nums">{formatoBytes(resumen.tamanoBytes)}</span>
            </div>
            <div>
              <span className="block text-xs text-gray-500">Colección</span>
              <span className="font-mono text-xs">{resumen.coleccion}</span>
            </div>
          </div>
        )}
      </div>

      {/* Filtros */}
      <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm space-y-3">
        <div className="flex flex-col md:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
            <input
              ref={inputRef}
              value={entrada}
              onChange={(e) => {
                setEntrada(e.target.value);
                setAbierto(true);
              }}
              onFocus={() => setAbierto(true)}
              onBlur={() => setTimeout(() => setAbierto(false), 150)}
              onKeyDown={onKeyDown}
              placeholder="campo:valor — p. ej. service:acta, hostname:web, level:50"
              role="combobox"
              aria-expanded={abierto && sugerencias.length > 0}
              className="w-full bg-white border border-gray-200 rounded-lg pl-9 pr-3 py-2 text-sm font-mono text-gray-700 outline-none focus:ring-2 focus:ring-blue-500"
            />
            {abierto && sugerencias.length > 0 && (
              <ul
                role="listbox"
                className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg py-1 max-h-72 overflow-auto"
              >
                {sugerencias.map((s, i) => (
                  <li
                    key={s.tipo === 'campo' ? s.campo : `${s.campo}=${s.valor}`}
                    role="option"
                    aria-selected={i === resaltado}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      elegir(s);
                    }}
                    onMouseEnter={() => setResaltado(i)}
                    className={`flex justify-between gap-3 px-3 py-1.5 text-sm cursor-pointer ${i === resaltado ? 'bg-blue-50' : ''}`}
                  >
                    <span className="font-mono truncate">{s.tipo === 'campo' ? `${s.campo}:` : s.valor}</span>
                    <span className="text-xs text-gray-400 shrink-0 tabular-nums">{s.detalle}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <Button
            variant="secondary"
            onClick={() => setOpcionesCampo(campoEntrada)}
            disabled={!campoEntrada || !/^[A-Za-z_][\w.]*$/.test(campoEntrada)}
          >
            <ListFilter className="w-4 h-4 mr-2" />
            Ver opciones
          </Button>
        </div>

        {filtros.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {filtros.map((f, i) => (
              <span
                key={i}
                className="flex items-center gap-1.5 rounded-lg bg-blue-50 border border-blue-200 pl-2 pr-1 py-1 text-xs text-blue-900"
              >
                <span className="font-mono font-semibold">{f.campo}</span>
                <select
                  value={f.op}
                  onChange={(e) => setFiltros((prev) => prev.map((p, k) => (k === i ? { ...p, op: e.target.value as OperadorLog } : p)))}
                  className="bg-transparent text-xs border-none p-0 focus:ring-0"
                  aria-label="Operador"
                >
                  {OPERADORES.map((o) => (
                    <option key={o.op} value={o.op}>
                      {o.label}
                    </option>
                  ))}
                </select>
                {!SIN_VALOR.includes(f.op) && <span className="font-mono">{f.valor}</span>}
                <button
                  onClick={() => setFiltros((prev) => prev.filter((_, k) => k !== i))}
                  aria-label="Quitar filtro"
                  className="p-0.5 hover:text-blue-600"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="flex flex-col md:flex-row md:items-end gap-3">
          <label className="text-xs text-gray-500">
            Desde
            <input
              type="datetime-local"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
              className="block mt-1 border border-gray-200 rounded-lg px-2 py-1.5 text-sm"
            />
          </label>
          <label className="text-xs text-gray-500">
            Hasta
            <input
              type="datetime-local"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
              className="block mt-1 border border-gray-200 rounded-lg px-2 py-1.5 text-sm"
            />
          </label>
          <div className="md:ml-auto flex gap-2">
            <Button onClick={() => buscar(1)} disabled={buscando}>
              {buscando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Filter className="w-4 h-4 mr-2" />}
              Filtrar
            </Button>
            <Button
              variant="danger"
              onClick={() => setConfirmarAbierto(true)}
              disabled={!resultadoVigente || !resultado?.total || (filtros.length === 0 && !desde && !hasta)}
              title={!resultadoVigente ? 'Filtra primero: se elimina exactamente lo que se ve' : undefined}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Eliminar {resultadoVigente && resultado ? resultado.total.toLocaleString('es-CO') : ''} logs
            </Button>
          </div>
        </div>
        {resultado && !resultadoVigente && (
          <p className="text-xs text-amber-700">Cambiaste los filtros: vuelve a filtrar para ver y eliminar.</p>
        )}
      </div>

      {errorGeneral && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{errorGeneral}</div>}
      {mensaje && <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{mensaje}</div>}

      {/* Resultados */}
      {resultado && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between gap-3 p-3 border-b border-gray-100 text-sm">
            <span>
              <strong className="tabular-nums">{resultado.total.toLocaleString('es-CO')}</strong> logs con este filtro
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  const nuevo = dir === 'desc' ? 'asc' : 'desc';
                  setDir(nuevo);
                  void buscar(1, nuevo);
                }}
                className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900"
              >
                <ArrowDownUp className="w-3.5 h-3.5" />
                {dir === 'desc' ? 'Más nuevos primero' : 'Más antiguos primero'}
              </button>
              <Paginacion pagina={pagina} total={totalPaginas} onCambiar={(p) => buscar(p)} />
            </div>
          </div>
          <div className="overflow-x-auto max-h-[560px]">
            <table className="w-full text-left text-xs text-gray-600">
              <thead className="bg-gray-50 text-gray-400 uppercase tracking-wider sticky top-0">
                <tr>
                  <th className="py-2 px-3">Fecha</th>
                  <th className="py-2 px-3">Nivel</th>
                  <th className="py-2 px-3">Servicio</th>
                  <th className="py-2 px-3">Hostname</th>
                  <th className="py-2 px-3">Mensaje</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {resultado.items.map((log) => (
                  <FilaLog
                    key={log._mongoId}
                    log={log}
                    abierto={expandido === log._mongoId}
                    onToggle={() => setExpandido(expandido === log._mongoId ? null : log._mongoId)}
                  />
                ))}
                {resultado.items.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-gray-400">
                      No hay logs con este filtro.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {opcionesCampo && (
        <ModalOpciones
          campo={opcionesCampo}
          consulta={consulta}
          onCerrar={() => setOpcionesCampo(null)}
          onElegir={(valor) => {
            agregarFiltro({ campo: opcionesCampo, op: 'igual', valor });
            setOpcionesCampo(null);
          }}
        />
      )}

      <Modal
        isOpen={confirmarAbierto}
        onClose={() => setConfirmarAbierto(false)}
        title="Eliminar logs"
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setConfirmarAbierto(false)}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={eliminar}>
              Eliminar definitivamente
            </Button>
          </div>
        }
      >
        <p className="text-sm text-gray-700">
          Se eliminarán <strong>{resultado?.total.toLocaleString('es-CO')}</strong> logs que cumplen:
        </p>
        <ul className="mt-2 space-y-1 text-xs font-mono text-gray-700">
          {filtros.map((f, i) => (
            <li key={i}>
              {f.campo} {OPERADORES.find((o) => o.op === f.op)?.label} {SIN_VALOR.includes(f.op) ? '' : f.valor}
            </li>
          ))}
          {desde && <li>desde {formatoFecha(aMs(desde))}</li>}
          {hasta && <li>hasta {formatoFecha(aMs(hasta))}</li>}
        </ul>
        <p className="mt-3 text-sm text-red-700">No se puede deshacer.</p>
      </Modal>
    </div>
  );
}

function Paginacion({ pagina, total, onCambiar }: { pagina: number; total: number; onCambiar: (p: number) => void }) {
  return (
    <div className="flex items-center gap-1 text-xs">
      <button
        onClick={() => onCambiar(pagina - 1)}
        disabled={pagina <= 1}
        className="p-1 rounded hover:bg-gray-100 disabled:opacity-40"
        aria-label="Página anterior"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>
      <span className="tabular-nums">
        {pagina} / {total}
      </span>
      <button
        onClick={() => onCambiar(pagina + 1)}
        disabled={pagina >= total}
        className="p-1 rounded hover:bg-gray-100 disabled:opacity-40"
        aria-label="Página siguiente"
      >
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}

function FilaLog({ log, abierto, onToggle }: { log: BusquedaLogsDto['items'][number]; abierto: boolean; onToggle: () => void }) {
  const nivel = log.level !== undefined ? NIVELES[log.level] : undefined;
  return (
    <>
      <tr onClick={onToggle} className="hover:bg-gray-50 cursor-pointer">
        <td className="py-1.5 px-3 whitespace-nowrap tabular-nums">{formatoFecha(log.time)}</td>
        <td className="py-1.5 px-3">{nivel ? <Badge variant={nivel.variant}>{nivel.label}</Badge> : (log.level ?? '—')}</td>
        <td className="py-1.5 px-3 font-mono">{log.service ?? '—'}</td>
        <td className="py-1.5 px-3 font-mono">{log.hostname ?? '—'}</td>
        <td className="py-1.5 px-3 max-w-xl truncate">{log.msg}</td>
      </tr>
      {abierto && (
        <tr>
          <td colSpan={5} className="bg-gray-50 px-3 py-2">
            <pre className="text-[11px] whitespace-pre-wrap break-all max-h-80 overflow-auto">{JSON.stringify(log, null, 2)}</pre>
          </td>
        </tr>
      )}
    </>
  );
}

function ModalOpciones({
  campo,
  consulta,
  onCerrar,
  onElegir,
}: {
  campo: string;
  consulta: ConsultaLogs;
  onCerrar: () => void;
  onElegir: (valor: string) => void;
}) {
  const [prefijo, setPrefijo] = useState('');
  const [page, setPage] = useState(1);
  const [orden, setOrden] = useState<'cantidad' | 'valor'>('cantidad');
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');
  const [data, setData] = useState<ValoresCampoDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const PAGE = 20;

  useEffect(() => {
    const t = setTimeout(() => {
      logsMongoService
        .valores({ ...consulta, campo, prefijo: prefijo || undefined, page, pageSize: PAGE, orden, dir })
        .then((r) => {
          setData(r);
          setError(null);
        })
        .catch((e) => setError(e.message));
    }, 200);
    return () => clearTimeout(t);
  }, [campo, prefijo, page, orden, dir, JSON.stringify(consulta)]);

  const ordenarPor = (o: 'cantidad' | 'valor') => {
    if (o === orden) setDir(dir === 'asc' ? 'desc' : 'asc');
    else {
      setOrden(o);
      setDir(o === 'cantidad' ? 'desc' : 'asc');
    }
    setPage(1);
  };
  const flecha = (o: 'cantidad' | 'valor') => (orden === o ? (dir === 'asc' ? ' ↑' : ' ↓') : '');

  return (
    <Modal isOpen onClose={onCerrar} title={<span className="font-mono">{campo}</span>} size="lg">
      <input
        autoFocus
        value={prefijo}
        onChange={(e) => {
          setPrefijo(e.target.value);
          setPage(1);
        }}
        placeholder="Empieza por…"
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono mb-3"
      />
      {error && <p className="text-sm text-red-700 mb-2">{error}</p>}
      <table className="w-full text-sm">
        <thead className="text-xs text-gray-500 border-b border-gray-200">
          <tr>
            <th className="text-left py-1.5 cursor-pointer select-none" onClick={() => ordenarPor('valor')}>
              Valor{flecha('valor')}
            </th>
            <th className="text-right py-1.5 cursor-pointer select-none" onClick={() => ordenarPor('cantidad')}>
              Logs{flecha('cantidad')}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {data?.items.map((i) => (
            <tr key={i.valor} onClick={() => onElegir(i.valor)} className="hover:bg-blue-50 cursor-pointer">
              <td className="py-1.5 font-mono break-all">{i.valor}</td>
              <td className="py-1.5 text-right tabular-nums">{i.cantidad.toLocaleString('es-CO')}</td>
            </tr>
          ))}
          {data && data.items.length === 0 && (
            <tr>
              <td colSpan={2} className="py-6 text-center text-gray-400">
                Sin valores
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {data && (
        <div className="flex justify-between items-center mt-3 text-xs text-gray-500">
          <span>{data.total.toLocaleString('es-CO')} valores distintos · clic en uno para agregarlo como filtro</span>
          <Paginacion pagina={page} total={Math.max(1, Math.ceil(data.total / PAGE))} onCambiar={setPage} />
        </div>
      )}
    </Modal>
  );
}
